/**
 * @file DropDownMenu.jsx
 * @module src/js/components/DropDownMenu
 * @description
 * **A reusable dropdown menu component.**
 * - Displays a title or custom element that, when hovered over (or tapped on touchscreens), reveals a dropdown menu.
 * - The dropdown menu content is provided as markdown text, allowing for rich formatting.
 * - The dropdown's position can be adjusted using `top` and `left` props.
 * - Adapts to light and dark themes using the application's theme context.
 * - Handles touch screen interactions by toggling visibility on tap and hiding the menu when tapping outside.
 * - Both `element` and `title` props are supported, but only one should be provided at a time.
 *
 * @version
 * <p>
 *  2025-10-18: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useRef, useState } from "react";
import { useTheme } from "../contexts/ThemeProvider";
import { ExtendedMarkdown } from "./UtilityComponents";
import getDevice from "../functions/getDevice";

export default function DropDownMenu({
    title = null,
    element = null,
    dropDownContent,
    top,
    left
}) {
    const { theme } = useTheme();
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const dropdownRef = useRef(null);
    const { hasTouchScreen } = getDevice();

    const transitionDuration = 200; // in milliseconds

    useEffect(() => {
        if (!hasTouchScreen) return;
        const handleClickOutsideDropdown = (e) => {
            if (!dropdownRef.current?.contains(e.target)) {
                setIsDropdownVisible(false);
            }
        };
        document.addEventListener("touchstart", handleClickOutsideDropdown);
        return () => document.removeEventListener("touchstart", handleClickOutsideDropdown);
    }, [hasTouchScreen]);

    // Either title or element is provided, not both or neither
    if ((title === null) === (element === null)) {
        console.error("DropDownMenu: Please provide either 'title' or 'element', but not both or neither.");
        return null;
    }

    return (
        <span
            ref={dropdownRef}
            style={{ position: "relative", display: "inline-block" }}
            onMouseLeave={hasTouchScreen ? null : () => setIsDropdownVisible(false)}
        >
            <span
                style={{ cursor: "pointer" }}
                onMouseEnter={hasTouchScreen ? null : () => setIsDropdownVisible(true)}
                onClick={hasTouchScreen ? () => setIsDropdownVisible((prev) => !prev) : null}
            >{title ? <h2>{isDropdownVisible ? `▼ ${title} ▼` : `▲ ${title} ▲`}</h2> : element}</span>
            <div style={{
                position: "absolute",
                top,
                left,
                transform: "translateX(-50%)",   // <-- this centers it
                backgroundColor: theme === "dark" ? `rgba(46,56,66,1)` : `rgba(240,240,240,1)`,
                border: `1px solid ${theme === "dark" ? `rgba(240,240,240,1)` : `rgba(46,56,66,1)`}`,
                color: theme === "dark" ? `rgba(240,240,240,1)` : `rgba(46,56,66,1)`,
                textAlign: "left",
                padding: "8px",
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                zIndex: 1000,
                borderRadius: "3px",
                opacity: isDropdownVisible ? 1 : 0,
                visibility: isDropdownVisible ? "visible" : "hidden",
                transition: `opacity ${transitionDuration}ms ease-in-out, visibility ${transitionDuration}ms ease-in-out`,
                whiteSpace: "nowrap", // not break into multiple lines
                fontSize: "0.75em"
            }}>
                <ExtendedMarkdown>{dropDownContent}</ExtendedMarkdown>
            </div>
        </span>
    )
}